import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { getEixoHelp, getIndicadorHelp, EixoHelp, IndicadorHelp } from '../../data/indicadores-help';

interface IndicadorHelpDialogProps {
  open: boolean;
  onClose: () => void;
  eixo: string;
  /** Se informado, mostra info do indicador especifico. Caso contrario, mostra visao geral do eixo. */
  indicador?: string;
}

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2.5 }}>
      <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700} color="primary">
        {title}
      </Typography>
    </Box>
  );
};

const IndicadorCard: React.FC<{ ind: IndicadorHelp; compact?: boolean }> = ({ ind, compact }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        bgcolor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {ind.chave} - {ind.nome}
        </Typography>
        <Chip
          label={`Peso: ${ind.peso}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22, ml: 1, flexShrink: 0 }}
        />
      </Box>
      {!compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.5 }}>
          {ind.descricao}
        </Typography>
      )}
      {compact && (
        <Typography variant="caption" color="text.secondary">
          {ind.periodo}
        </Typography>
      )}
    </Box>
  );
};

const IndicadorHelpDialog: React.FC<IndicadorHelpDialogProps> = ({ open, onClose, eixo, indicador }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const eixoData = getEixoHelp(eixo);

  if (!eixoData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          Ajuda
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Informacoes de ajuda nao disponiveis para este eixo ({eixo}).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Se indicador especificado, mostra detalhes do indicador
  const indData = indicador ? getIndicadorHelp(eixo, indicador) : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h6" fontWeight={700} component="span">
            {indData ? `${indData.chave} - ${indData.nome}` : eixoData.nome}
          </Typography>
          {indData && (
            <Typography variant="caption" display="block" color="text.secondary">
              {eixoData.nome}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {indData ? (
          <IndicadorDetail eixoData={eixoData} indData={indData} />
        ) : (
          <EixoOverview eixoData={eixoData} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Fonte: Notas Metodologicas - Cofinanciamento APS / Ministerio da Saude
        </Typography>
        <Button onClick={onClose} variant="outlined" size="small">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Visao geral do eixo (quando nao ha indicador especifico) */
const EixoOverview: React.FC<{ eixoData: EixoHelp }> = ({ eixoData }) => {
  const theme = useTheme();

  const tipoLabel: Record<string, string> = {
    'boas-praticas': 'Boas Praticas (per cidadao)',
    taxa: 'Taxa / Proporcao',
    producao: 'Producao (contagens)',
    vinculo: 'Vinculo Territorial',
  };

  return (
    <Box>
      {/* Descricao */}
      <SectionTitle icon={<InfoOutlinedIcon fontSize="small" />} title="O que e medido" />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {eixoData.descricao}
      </Typography>

      {/* Populacao e tipo */}
      <SectionTitle icon={<PeopleOutlinedIcon fontSize="small" />} title="Populacao-alvo" />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {eixoData.populacao}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Chip
          label={`Tipo: ${tipoLabel[eixoData.tipo] || eixoData.tipo}`}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 600 }}
        />
        <Chip
          label={`Score total: ${eixoData.scoreTotal} pontos`}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 600 }}
        />
      </Box>

      {/* Classificacao */}
      <SectionTitle icon={<SpeedOutlinedIcon fontSize="small" />} title="Classificacao" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {eixoData.classificacao.map((c) => (
          <Chip
            key={c.label}
            label={`${c.label}: ${c.faixa}`}
            size="small"
            sx={{
              bgcolor: alpha(c.cor, 0.1),
              color: c.cor,
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 26,
            }}
          />
        ))}
      </Box>

      {/* Lista de indicadores */}
      <SectionTitle icon={<CalculateOutlinedIcon fontSize="small" />} title="Indicadores" />
      {eixoData.indicadores.map((ind) => (
        <IndicadorCard key={ind.chave} ind={ind} compact />
      ))}
    </Box>
  );
};

/** Detalhes de um indicador especifico */
const IndicadorDetail: React.FC<{ eixoData: EixoHelp; indData: IndicadorHelp }> = ({
  eixoData,
  indData,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Descricao */}
      <SectionTitle icon={<InfoOutlinedIcon fontSize="small" />} title="O que e medido" />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {indData.descricao}
      </Typography>

      {/* Como e calculado */}
      <SectionTitle icon={<CalculateOutlinedIcon fontSize="small" />} title="Como e calculado" />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {indData.calculo}
      </Typography>

      {/* Periodo e peso */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}
          label={indData.periodo}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.info.main, 0.08), fontWeight: 500 }}
        />
        <Chip
          label={`Peso: ${indData.peso} pontos (de ${eixoData.scoreTotal})`}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), fontWeight: 500 }}
        />
      </Box>

      {/* Fonte dos dados */}
      {indData.fontes.length > 0 && (
        <>
          <SectionTitle icon={<StorageOutlinedIcon fontSize="small" />} title="Fonte dos dados" />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {indData.fontes.map((f) => (
              <Chip
                key={f}
                label={f}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', fontFamily: 'monospace', height: 24 }}
              />
            ))}
          </Box>
        </>
      )}

      {/* Codigos utilizados */}
      {indData.codigos.length > 0 && (
        <>
          <SectionTitle icon={<CodeOutlinedIcon fontSize="small" />} title="Codigos utilizados" />
          <TableContainer
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              maxHeight: 260,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    Codigo
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    Descricao
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indData.codigos.map((c, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {c.codigo}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{c.descricao}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Classificacao do eixo */}
      <SectionTitle icon={<SpeedOutlinedIcon fontSize="small" />} title="Classificacao do eixo" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {eixoData.classificacao.map((c) => (
          <Chip
            key={c.label}
            label={`${c.label}: ${c.faixa}`}
            size="small"
            sx={{
              bgcolor: alpha(c.cor, 0.1),
              color: c.cor,
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 26,
            }}
          />
        ))}
      </Box>

      <Divider sx={{ mt: 3, mb: 1 }} />
      <Typography variant="caption" color="text.secondary">
        Populacao-alvo do eixo: {eixoData.populacao}
      </Typography>
    </Box>
  );
};

export default IndicadorHelpDialog;
